import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError, CITIES_BY_COUNTRY, COUNTRY_LABELS, PROPERTY_TYPES, AMENITIES_LIST } from "@/lib/api";
import { NEW_LISTING } from "@/constants/testIds";
import { toast } from "sonner";
import { ArrowLeft, Sparkles, Loader2 } from "lucide-react";
import { ImageUploader } from "@/components/ImageUploader";

const DEFAULT = {
  country: "MA",
  type: "vente",
  property_type: "appartement",
  title: "",
  description: "",
  price: "",
  surface: "",
  rooms: "",
  bathrooms: "",
  city: "",
  neighborhood: "",
  photos: [],
  amenities: [],
  latitude: "",
  longitude: "",
};

export default function NewListing() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [form, setForm] = useState(DEFAULT);
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (loading) return;
    if (!user) navigate("/login");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      const { data } = await api.get(`/listings/${id}`);
      setForm({
        ...DEFAULT,
        ...data,
        photos: data.photos || [],
        rooms: data.rooms ?? "",
        bathrooms: data.bathrooms ?? "",
        latitude: data.latitude ?? "",
        longitude: data.longitude ?? "",
      });
    })();
  }, [id, isEdit]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleAmenity = (a) =>
    setForm((f) => ({ ...f, amenities: f.amenities.includes(a) ? f.amenities.filter((x) => x !== a) : [...f.amenities, a] }));

  const generateAI = async () => {
    if (!form.city || !form.surface) {
      toast.error("Renseignez d'abord la ville et la surface");
      return;
    }
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/generate-description", {
        property_type: form.property_type,
        type: form.type,
        country: form.country,
        city: form.city,
        neighborhood: form.neighborhood || null,
        surface: parseFloat(form.surface),
        rooms: form.rooms ? parseInt(form.rooms) : null,
        bathrooms: form.bathrooms ? parseInt(form.bathrooms) : null,
        amenities: form.amenities,
      });
      update("description", data.description);
      toast.success("Description générée par IA ✨");
    } catch (err) {
      toast.error("Erreur IA : " + formatApiError(err));
    } finally {
      setAiBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const payload = {
      country: form.country,
      type: form.type,
      property_type: form.property_type,
      title: form.title,
      description: form.description,
      price: parseFloat(form.price),
      surface: parseFloat(form.surface),
      rooms: form.rooms ? parseInt(form.rooms) : null,
      bathrooms: form.bathrooms ? parseInt(form.bathrooms) : null,
      city: form.city,
      neighborhood: form.neighborhood || null,
      photos: form.photos,
      amenities: form.amenities,
      latitude: form.latitude ? parseFloat(form.latitude) : null,
      longitude: form.longitude ? parseFloat(form.longitude) : null,
    };
    try {
      if (isEdit) {
        await api.put(`/listings/${id}`, payload);
        toast.success("Annonce mise à jour");
      } else {
        await api.post("/listings", payload);
        toast.success("Annonce publiée !");
      }
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const cities = CITIES_BY_COUNTRY[form.country] || [];

  return (
    <Layout withFooter={false}>
      <div className="max-w-3xl mx-auto px-6 lg:px-8 py-8">
        <button onClick={() => navigate("/dashboard")} className="inline-flex items-center text-sm text-muted-foreground hover:text-[#D6202C] mb-4">
          <ArrowLeft className="w-4 h-4 mr-1" /> Retour
        </button>

        <span className="text-xs tracking-[0.2em] uppercase text-[#11783A] font-medium">
          {isEdit ? "Modifier" : "Nouvelle annonce"}
        </span>
        <h1 className="font-heading text-4xl md:text-5xl font-medium tracking-tight mt-2 mb-8">
          {isEdit ? "Modifier l'annonce" : "Publier une annonce"}
        </h1>

        <form onSubmit={submit} className="bg-white border border-[#E5E5E5] rounded-2xl p-6 space-y-5">
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <Label>Pays</Label>
              <Select value={form.country} onValueChange={(v) => update("country", v)}>
                <SelectTrigger data-testid={NEW_LISTING.country} className="mt-1 h-11 rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(COUNTRY_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Transaction</Label>
              <Select value={form.type} onValueChange={(v) => update("type", v)}>
                <SelectTrigger data-testid={NEW_LISTING.type} className="mt-1 h-11 rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="vente">Vente</SelectItem>
                  <SelectItem value="location">Location</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Type de bien</Label>
              <Select value={form.property_type} onValueChange={(v) => update("property_type", v)}>
                <SelectTrigger data-testid={NEW_LISTING.propertyType} className="mt-1 h-11 rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PROPERTY_TYPES.map((p) => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>Titre de l'annonce</Label>
            <Input
              data-testid={NEW_LISTING.title}
              value={form.title}
              onChange={(e) => update("title", e.target.value)}
              required
              className="mt-1 rounded-xl h-11"
              placeholder="Ex: Magnifique villa avec piscine - Marrakech"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <Label>Description</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={generateAI}
                disabled={aiBusy}
                data-testid="ai-generate-btn"
                className="h-8 rounded-full text-xs border-[#D6202C] text-[#D6202C] hover:bg-[#FEF2F3]"
              >
                {aiBusy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
                {aiBusy ? "Génération..." : "Générer avec IA"}
              </Button>
            </div>
            <Textarea
              data-testid={NEW_LISTING.description}
              value={form.description}
              onChange={(e) => update("description", e.target.value)}
              rows={5}
              required
              minLength={10}
              className="mt-1 rounded-xl"
              placeholder="Décrivez votre bien, le quartier, les équipements... ou cliquez sur 'Générer avec IA'."
            />
          </div>

          <div className="grid sm:grid-cols-4 gap-4">
            <div>
              <Label>Prix</Label>
              <Input
                data-testid={NEW_LISTING.price}
                value={form.price}
                onChange={(e) => update("price", e.target.value)}
                type="number"
                required
                className="mt-1 rounded-xl h-11"
                placeholder="1000000"
              />
            </div>
            <div>
              <Label>Surface (m²)</Label>
              <Input
                data-testid={NEW_LISTING.surface}
                value={form.surface}
                onChange={(e) => update("surface", e.target.value)}
                type="number"
                required
                className="mt-1 rounded-xl h-11"
              />
            </div>
            <div>
              <Label>Pièces</Label>
              <Input
                data-testid={NEW_LISTING.rooms}
                value={form.rooms}
                onChange={(e) => update("rooms", e.target.value)}
                type="number"
                className="mt-1 rounded-xl h-11"
              />
            </div>
            <div>
              <Label>Salles de bain</Label>
              <Input
                data-testid={NEW_LISTING.bathrooms}
                value={form.bathrooms}
                onChange={(e) => update("bathrooms", e.target.value)}
                type="number"
                className="mt-1 rounded-xl h-11"
              />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Ville</Label>
              <Select value={form.city} onValueChange={(v) => update("city", v)}>
                <SelectTrigger data-testid={NEW_LISTING.city} className="mt-1 h-11 rounded-xl">
                  <SelectValue placeholder="Choisir une ville" />
                </SelectTrigger>
                <SelectContent>
                  {cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Quartier (optionnel)</Label>
              <Input
                data-testid={NEW_LISTING.neighborhood}
                value={form.neighborhood}
                onChange={(e) => update("neighborhood", e.target.value)}
                className="mt-1 rounded-xl h-11"
              />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Latitude (optionnel, pour la carte)</Label>
              <Input
                value={form.latitude}
                onChange={(e) => update("latitude", e.target.value)}
                type="number"
                step="any"
                className="mt-1 rounded-xl h-11"
                placeholder="33.5731"
              />
            </div>
            <div>
              <Label>Longitude (optionnel)</Label>
              <Input
                value={form.longitude}
                onChange={(e) => update("longitude", e.target.value)}
                type="number"
                step="any"
                className="mt-1 rounded-xl h-11"
                placeholder="-7.6594"
              />
            </div>
          </div>

          <div>
            <Label>Photos</Label>
            <div className="mt-2" data-testid={NEW_LISTING.photos}>
              <ImageUploader urls={form.photos} onChange={(urls) => update("photos", urls)} max={10} />
            </div>
          </div>

          <div data-testid={NEW_LISTING.amenities}>
            <Label>Équipements</Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
              {AMENITIES_LIST.map((a) => (
                <label key={a} className="flex items-center gap-2 cursor-pointer text-sm">
                  <Checkbox
                    checked={form.amenities.includes(a)}
                    onCheckedChange={() => toggleAmenity(a)}
                  />
                  <span>{a}</span>
                </label>
              ))}
            </div>
          </div>

          {error && <div className="text-sm text-red-600">{error}</div>}

          <Button
            type="submit"
            data-testid={NEW_LISTING.submit}
            disabled={busy}
            className="w-full h-12 rounded-xl bg-[#D6202C] hover:bg-[#B5181F] text-white"
          >
            {busy ? "Publication..." : isEdit ? "Mettre à jour" : "Publier l'annonce"}
          </Button>
        </form>
      </div>
    </Layout>
  );
}
